import React, { useEffect, useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';
import PageLoader from '../components/Loader/PageLoader';
import { shortAddress, formatTimestamp, copyToClipboard, formatUSDT } from '../utils/format';
import { notify } from '../utils/notify';

const Row = ({ label, value, mono = true, onCopy }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid var(--border-secondary)',
    gap: 16
  }}>
    <div style={{
      fontSize: 12,
      color: 'var(--text-muted)',
      fontFamily: 'Orbitron, sans-serif',
      letterSpacing: '0.1em',
      textTransform: 'uppercase'
    }}>{label}</div>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
      fontSize: 14,
      color: 'var(--text-primary)',
      wordBreak: 'break-all',
      textAlign: 'right'
    }}>
      {value}
      {onCopy && (
        <button
          onClick={onCopy}
          style={{
            background: 'transparent',
            border: '1px solid var(--border-secondary)',
            color: 'var(--accent-cyan)',
            padding: '4px 8px',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 11
          }}
        >Copy</button>
      )}
    </div>
  </div>
);

const Profile = () => {
  const { contract, account, userId, network, disconnect } = useWeb3();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);

  const load = async () => {
    if (!contract || !userId) { setLoading(false); return; }
    try {
      const [basic, ext, stats] = await Promise.all([
        contract.getUserBasicInfo(userId),
        contract.getUserExtendedInfo(userId),
        contract.userStats(userId)
      ]);
      setInfo({
        wallet: basic.wallet,
        totalInvestment: basic.totalInvestment.toString(),
        totalEarned: basic.totalEarned.toString(),
        directReferrals: ext.directReferrals.toString(),
        referrerId: ext.referrerId.toString(),
        active: ext.active,
        registrationTime: stats.registrationTime.toString()
      });
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [contract, account, userId]);

  const copy = async (text, label) => {
    const ok = await copyToClipboard(text);
    if (ok) notify.success(`${label} copied`);
  };

  if (loading) return <PageLoader text="Loading Profile" />;

  if (!account) {
    return (
      <Card>
        <div className="empty-state">
          <div className="empty-state-icon">◌</div>
          <div className="empty-state-text">Connect your wallet</div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <h1 className="page-title">Profile</h1>
      <p className="page-subtitle">Your account details</p>

      <div className="grid grid-2">
        <Card title="Account">
          <Row label="Wallet" value={shortAddress(account)} onCopy={() => copy(account, 'Address')} />
          <Row label="User ID" value={userId ? `#${userId}` : 'Not registered'} />
          {info && info.referrerId !== '0' && (
            <Row label="Referrer ID" value={`#${info.referrerId}`} />
          )}
          {info && (
            <Row
              label="Registered"
              value={formatTimestamp(info.registrationTime)}
              mono={false}
            />
          )}
          <Row
            label="Network"
            value={network.displayName}
            mono={false}
          />
          <div style={{ marginTop: 16 }}>
            <Button variant="danger" size="sm" onClick={disconnect}>Disconnect Wallet</Button>
          </div>
        </Card>

        {info && (
          <Card title="Performance">
            <Row label="Total Invested" value={`${formatUSDT(info.totalInvestment)} USDT`} />
            <Row label="Total Earned" value={`${formatUSDT(info.totalEarned)} USDT`} />
            <Row label="Direct Referrals" value={info.directReferrals} />
            <Row
              label="Status"
              value={info.active ? 'Active' : 'Inactive'}
              mono={false}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export default Profile;
